import { useId, useState, type FormEvent } from "react";
import { Button, ErrorText } from "../../ui";
import { Dialog } from "../../ui/overlay";
import { parseLocalDateTime, snoozeChoices, toLocalDateTime } from "./snooze-model";

/**
 * `z`: snooze the conversation (archived now, back in the inbox, unread, at the chosen time).
 * Quick choices, or a date and time of the person's own.
 */
export function SnoozeDialog({ now, onSnooze, onClose }: { now: Date; onSnooze: (until: Date) => void; onClose: () => void }) {
  const id = useId();
  const choices = snoozeChoices(now);
  const [custom, setCustom] = useState(() => toLocalDateTime(choices[1] === undefined ? now : choices[1].until));
  const [error, setError] = useState<string | null>(null);

  const pick = (until: Date) => {
    onSnooze(until);
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const until = parseLocalDateTime(custom);
    if (until === null || until.getTime() <= now.getTime() + 60_000) {
      setError("Pick a date and time in the future.");
      return;
    }
    pick(until);
  };

  return (
    <Dialog title="Snooze until…" description="It leaves the inbox now and comes back, unread, at that time." onClose={onClose}>
      <div className="flex flex-col gap-3">
        <ul aria-label="Snooze choices" className="flex flex-col gap-1">
          {choices.map((c) => (
            <li key={c.key}>
              <Button className="w-full justify-start" onClick={() => pick(c.until)}>
                {c.label}
              </Button>
            </li>
          ))}
        </ul>
        <form onSubmit={submit} className="flex flex-col gap-2 border-t border-border pt-3" noValidate>
          <label htmlFor={id} className="text-sm font-medium text-text">
            Pick a date and time
          </label>
          <div className="flex gap-2">
            <input
              id={id}
              type="datetime-local"
              value={custom}
              min={toLocalDateTime(now)}
              onChange={(e) => setCustom(e.target.value)}
              className="min-h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-3 text-sm text-text"
            />
            <Button type="submit" variant="primary">
              Snooze
            </Button>
          </div>
          {error === null ? null : <ErrorText>{error}</ErrorText>}
        </form>
      </div>
    </Dialog>
  );
}
